import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { api, apiBase, ApiError } from '../api';
import { useApiHealth, useAuth, useAuthConfig, useTheme } from '../hooks';

export function Login() {
  const { setLogin } = useAuth();
  const { tema, toggle } = useTheme();
  const { data: online } = useApiHealth();
  const { data: authCfg } = useAuthConfig();
  const navigate = useNavigate();

  const [usuario, setUsuario] = useState('');
  const [senha, setSenha] = useState('');
  const [erro, setErro] = useState<string | null>(null);
  const [carregando, setCarregando] = useState(false);

  // Motivo do redirect (sessão única derrubou a sessão antiga, ou token expirou).
  const motivo = typeof window !== 'undefined'
    ? new URLSearchParams(window.location.search).get('motivo')
    : null;

  async function entrar(e: React.FormEvent) {
    e.preventDefault();
    setErro(null);
    if (!usuario.trim() || !senha) {
      setErro('Informe usuário e senha.');
      return;
    }
    setCarregando(true);
    try {
      const s = await api.loginSenha(usuario.trim(), senha);
      setLogin({ id: s.id, nome: s.nome, papel: s.papel, precisa_trocar_senha: s.precisa_trocar_senha });
      navigate(s.precisa_trocar_senha ? '/trocar-senha' : '/semanal', { replace: true });
    } catch (err) {
      setErro(err instanceof ApiError && err.status === 401
        ? 'Usuário ou senha inválidos.'
        : 'Falha ao entrar. Tente novamente.');
      setSenha('');
    } finally {
      setCarregando(false);
    }
  }

  return (
    <div className="bg-gradient-to-br from-blue-50 via-white to-blue-100 dark:from-gray-900 dark:via-gray-900 dark:to-blue-950 min-h-screen flex items-center justify-center p-4 text-gray-800 dark:text-gray-100">
      <button
        onClick={toggle}
        className="fixed top-4 right-4 p-2 bg-white dark:bg-gray-800 rounded-full shadow hover:shadow-md text-gray-600 dark:text-gray-300"
      >
        {tema === 'dark' ? '☀️' : '🌙'}
      </button>

      <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-xl p-8 w-full max-w-md">
        {/* Login compartilhado entre TODOS os tenants — branding NEUTRO do produto,
            sem dados de nenhum escritório (o branding do escritório só aparece
            depois de autenticar, no kanban). */}
        <div className="text-center mb-6">
          <div className="h-16 w-16 mx-auto mb-3 rounded-2xl bg-blue-700 text-white flex items-center justify-center text-2xl font-bold shadow-md">
            ⚖️
          </div>
          <h1 className="text-xl font-bold">Agenda de Prazos</h1>
          <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
            Gestão de prazos processuais
          </p>
        </div>

        {motivo === 'sessao_encerrada' && !erro && (
          <div className="mb-4 text-xs text-amber-700 dark:text-amber-300 bg-amber-500/10 border border-amber-500/25 rounded-lg px-3 py-2">
            ⚠ Sua sessão foi encerrada. Isso acontece quando sua conta é acessada em
            outro dispositivo, ou após um período de inatividade. Entre novamente.
          </div>
        )}

        <form onSubmit={entrar} className="space-y-3">
          <div>
            <label className="block text-xs font-medium text-gray-600 dark:text-gray-300 mb-1">Usuário</label>
            <input
              type="text"
              value={usuario}
              onChange={(e) => setUsuario(e.target.value)}
              autoComplete="username"
              // eslint-disable-next-line jsx-a11y/no-autofocus
              autoFocus
              placeholder="seu nome de usuário"
              className="w-full rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-600 dark:text-gray-300 mb-1">Senha</label>
            <input
              type="password"
              value={senha}
              onChange={(e) => setSenha(e.target.value)}
              autoComplete="current-password"
              placeholder="••••••••"
              className="w-full rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>

          {erro && (
            <div className="text-xs text-red-600 dark:text-red-400 bg-red-500/10 border border-red-500/20 rounded-lg px-3 py-2">
              ⚠ {erro}
            </div>
          )}

          <button
            type="submit"
            disabled={carregando}
            className="w-full py-2.5 bg-blue-700 hover:bg-blue-800 disabled:opacity-50 text-white rounded-lg font-medium text-sm transition flex items-center justify-center gap-2"
          >
            {carregando ? <span className="animate-spin">⏳</span> : null}
            {carregando ? 'Entrando…' : 'Entrar'}
          </button>
        </form>

        {authCfg?.oauth_enabled && (
          <>
            <div className="my-4 flex items-center gap-3">
              <hr className="flex-1 border-gray-200 dark:border-gray-700" />
              <span className="text-[10px] text-gray-400 dark:text-gray-500 uppercase">ou</span>
              <hr className="flex-1 border-gray-200 dark:border-gray-700" />
            </div>
            <a
              href={`${apiBase()}/auth/login`}
              className="w-full flex items-center justify-center gap-3 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-lg py-2.5 px-4 text-sm font-medium text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-600 shadow-sm"
            >
              <svg width="18" height="18" viewBox="0 0 18 18" aria-hidden="true">
                <path fill="#4285F4" d="M17.64 9.2c0-.637-.057-1.251-.164-1.84H9v3.481h4.844a4.14 4.14 0 01-1.796 2.716v2.259h2.908c1.702-1.567 2.684-3.875 2.684-6.615z" />
                <path fill="#34A853" d="M9 18c2.43 0 4.467-.806 5.956-2.18l-2.908-2.259c-.806.54-1.837.86-3.048.86-2.344 0-4.328-1.584-5.036-3.71H.957v2.332A8.997 8.997 0 009 18z" />
                <path fill="#FBBC05" d="M3.964 10.71A5.41 5.41 0 013.682 9c0-.593.102-1.17.282-1.71V4.958H.957A8.996 8.996 0 000 9c0 1.452.348 2.827.957 4.042l3.007-2.332z" />
                <path fill="#EA4335" d="M9 3.58c1.321 0 2.508.454 3.44 1.345l2.582-2.58C13.463.891 11.426 0 9 0A8.997 8.997 0 00.957 4.958L3.964 7.29C4.672 5.163 6.656 3.58 9 3.58z" />
              </svg>
              Entrar com Google
            </a>
          </>
        )}

        <p className="mt-4 text-[11px] text-center text-gray-400 dark:text-gray-500">
          Primeiro acesso? Use a senha provisória informada pelo gestor e troque-a em seguida.
        </p>

        <div className="mt-4 text-[10px] text-center">
          {online === undefined ? (
            <span className="text-gray-400">⚪ Testando API…</span>
          ) : online ? (
            <span className="text-green-600 dark:text-green-400">
              🟢 API conectada: <code>{apiBase() || 'same-origin'}</code>
            </span>
          ) : (
            <span className="text-red-500 dark:text-red-400">
              🔴 API offline ({apiBase() || 'same-origin'})
            </span>
          )}
        </div>
      </div>
    </div>
  );
}
